/**
 * components/riskTerminal/columns.js
 *
 * upstox-074 (item 09) — ONE column configuration for the positions track: the header, leg, flat,
 * holdings and closed rows all read it (the widths used to be repeated in five places). When the
 * track — measured with a ResizeObserver on the track itself, not the viewport — is narrower than
 * its content, columns are hidden in this order: INTENT → CHARGES → STOP. LTP and
 * P&L are always visible, and the track never scrolls sideways.
 *
 * upstox-085 — the widths are the Claude Design handoff's `cols` (positions-wide-handoff-2026-11-04):
 * number columns are fixed, only the Structure / leg column grows. With every column shown the
 * "full" widths apply; once INTENT is hidden (a narrow track) the "narrow" set applies, as in the
 * handoff's 1440 frame. The ACTIONS column stays 60 px (the handoff draws 32): a leg row carries two
 * icons (move, exit) and the header carries expand / collapse all.
 */

import { createContext, useContext, useEffect, useState } from 'react';

export const COL_FULL = { intent: 56, qty: 56, avg: 72, ltp: 72, pl: 96, charges: 84, stop: 120, actions: 60, caret: 22, pad: 28 };
export const COL_NARROW = { ...COL_FULL, qty: 52, avg: 68, ltp: 68, pl: 92 };
export const COL = COL_FULL;
export const NAME_MIN = 234;                       // the name column never gets less than this (all columns shown ≈ 900 px of track)
export const HIDE_ORDER = ['intent', 'charges', 'stop'];

/** The columns shown at a track width: { intent, charges, stop } booleans (the rest always show). */
export function visibleColumns(trackWidth, extra = 0) {
  const vis = { intent: true, charges: true, stop: true };
  if (!trackWidth) return vis;
  const need = () => { const C = vis.intent ? COL_FULL : COL_NARROW; return extra + C.pad + C.caret + NAME_MIN + C.qty + C.avg + C.ltp + C.pl + C.actions
    + (vis.intent ? C.intent : 0) + (vis.charges ? C.charges : 0) + (vis.stop ? C.stop : 0); };
  for (const k of HIDE_ORDER) { if (need() > trackWidth) vis[k] = false; }
  return vis;
}

export const ColsCtx = createContext({ intent: true, charges: true, stop: true });
export const useCols = () => useContext(ColsCtx);

/** The width set in force: the narrow one once INTENT is hidden. */
export const widths = vis => (vis && vis.intent === false ? COL_NARROW : COL_FULL);
/** Width in px of a column's cell style. */
export const cw = (k, vis) => widths(vis)[k] + 'px';
export const cbox = (vis, k) => ({ width: cw(k, vis), flex: '0 0 ' + cw(k, vis) });
export const cell = (k, vis, extra) => ({ ...cbox(vis, k), ...extra });
/** CLOSED TODAY's AVG BUY spans the INTENT + QTY columns (QTY alone when INTENT is hidden). */
export const closedBuyW = vis => { const w = widths(vis); return (vis && vis.intent === false ? w.qty : w.intent + w.qty) + 'px'; };
/** The closed-today group note spans STOP + ACTIONS (ACTIONS alone when STOP is hidden). */
export const closedNoteW = vis => { const w = widths(vis); return (vis && vis.stop === false ? w.actions : w.stop + w.actions) + 'px'; };

/** Observe the track element (held in state, so it is picked up when it mounts) and return which columns fit. */
export function useTrackColumns(el, extra = 0) {
  const [vis, setVis] = useState(() => visibleColumns(null));
  useEffect(() => {
    if (!el) return undefined;
    const measure = () => {
      const next = visibleColumns(el.clientWidth, extra);
      setVis(v => (v.intent === next.intent && v.charges === next.charges && v.stop === next.stop ? v : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, extra]);
  return vis;
}
